import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { billingApi } from '../../../services/billingApi';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';
import { Alert, Button } from '../../../components/common';
import { StatTile, StatusBadge, SectionCard } from './components';
import { formatCurrency, formatDateOnly, kindLabel, formatDate } from './format';
import './BillingAdmin.css';

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUserHasCerebralAdminRole()) navigate('/user-home');
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingApi.getInvoice(invoiceId);
      setInvoice(data?.invoice || data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="user-home-layout__page">
        <Link to="/user-home/admin/billing/invoices" className="billing-admin__breadcrumb">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Invoices
        </Link>
        <div className="billing-admin__skeleton billing-admin__skeleton--lg" style={{ width: 240, marginTop: 16 }} />
        <div className="billing-admin__stats" style={{ marginTop: 24 }}>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="billing-admin__stat-tile">
              <div style={{ flex: 1 }}>
                <span className="billing-admin__skeleton billing-admin__skeleton--sm" style={{ width: 80 }} />
                <span className="billing-admin__skeleton billing-admin__skeleton--lg" style={{ marginTop: 8 }} />
              </div>
            </div>
          ))}
        </div>
        <div className="billing-admin__skeleton" style={{ height: 240, borderRadius: 8, marginTop: 16 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-home-layout__page">
        <Link to="/user-home/admin/billing/invoices" className="billing-admin__breadcrumb">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Invoices
        </Link>
        <Alert variant="error" title="Could not load invoice">{error}</Alert>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="user-home-layout__page">
        <Link to="/user-home/admin/billing/invoices" className="billing-admin__breadcrumb">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Invoices
        </Link>
        <Alert variant="error">Invoice not found.</Alert>
      </div>
    );
  }

  const lines = invoice.lineItems || invoice.line_items || [];
  const currency = invoice.Currency || invoice.currency || 'USD';
  const totalCents = invoice.TotalCents ?? invoice.total_cents ?? lines.reduce((s, li) => s + (li.AmountCents || li.amount_cents || 0), 0);
  const subtotalCents = invoice.SubtotalCents ?? invoice.subtotal_cents ?? totalCents;
  const taxCents = invoice.TaxCents ?? invoice.tax_cents ?? 0;
  const status = (invoice.Status || invoice.status || 'draft').toLowerCase();
  const periodStart = invoice.PeriodStart || invoice.period_start;
  const periodEnd = invoice.PeriodEnd || invoice.period_end;
  const generatedAt = invoice.GeneratedAt || invoice.generated_at || invoice.CreatedAt || invoice.created_at;

  return (
    <div className="user-home-layout__page">
      <Link to="/user-home/admin/billing/invoices" className="billing-admin__breadcrumb billing-admin__no-print">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        Invoices
      </Link>

      <div className="billing-admin__page-header billing-admin__no-print">
        <div className="billing-admin__header-text">
          <h1 className="user-home-layout__page-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>{invoice.InvoiceNumber || invoice.invoice_number || 'Invoice'}</span>
            <StatusBadge status={status} />
          </h1>
          <p className="user-home-layout__page-subtitle">
            Generated {formatDate(generatedAt)} for the billing period{' '}
            {formatDateOnly(periodStart)} → {formatDateOnly(periodEnd)}.
          </p>
        </div>
        <div className="billing-admin__header-actions">
          <Button variant="secondary" onClick={() => window.print()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6, verticalAlign: '-2px' }}>
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </Button>
          <a href={billingApi.invoiceCsvUrl(invoiceId)} download style={{ textDecoration: 'none' }}>
            <Button variant="primary">Download CSV</Button>
          </a>
        </div>
      </div>

      <div className="billing-admin__receipt">
        <header className="billing-admin__receipt-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <p className="billing-admin__stat-label" style={{ marginBottom: 4 }}>Invoice</p>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                {invoice.InvoiceNumber || invoice.invoice_number || invoiceId}
              </h2>
              <p className="billing-admin__muted" style={{ margin: '4px 0 0' }}>
                Issued by CerebralMachines on behalf of the platform.
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p className="billing-admin__stat-label" style={{ marginBottom: 4 }}>Bill to</p>
              <p style={{ margin: 0, fontWeight: 600 }}>
                {invoice.AccountName || invoice.account_name || <span className="billing-admin__muted">—</span>}
              </p>
              {invoice.CompanyName || invoice.company_name ? (
                <p style={{ margin: '2px 0 0', color: 'var(--uh-text-secondary)' }}>
                  {invoice.CompanyName || invoice.company_name}
                </p>
              ) : null}
            </div>
          </div>
        </header>

        <div className="billing-admin__receipt-meta">
          <div className="billing-admin__receipt-meta-cell">
            <span className="billing-admin__stat-label">Period</span>
            <span style={{ fontWeight: 600 }}>{formatDateOnly(periodStart)} → {formatDateOnly(periodEnd)}</span>
          </div>
          <div className="billing-admin__receipt-meta-cell">
            <span className="billing-admin__stat-label">Status</span>
            <span><StatusBadge status={status} /></span>
          </div>
          <div className="billing-admin__receipt-meta-cell">
            <span className="billing-admin__stat-label">Generated</span>
            <span style={{ fontWeight: 600 }}>{formatDateOnly(generatedAt)}</span>
          </div>
          <div className="billing-admin__receipt-meta-cell">
            <span className="billing-admin__stat-label">Currency</span>
            <span style={{ fontWeight: 600 }}>{currency}</span>
          </div>
        </div>

        <div style={{ padding: '0 28px' }}>
          <div className="billing-admin__stats" style={{ margin: '20px 0', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <StatTile label="Subtotal" value={formatCurrency(subtotalCents, currency)} />
            <StatTile label="Tax" value={formatCurrency(taxCents, currency)} sub={taxCents ? '' : 'No tax applied'} />
            <StatTile label="Line items" value={lines.length} sub="Distinct charges" />
          </div>
        </div>

        <div className="billing-admin__section-header" style={{ borderTop: '1px solid var(--uh-border-light)' }}>
          <h3 className="billing-admin__section-title">Line items</h3>
        </div>
        <div className="billing-admin__table-wrap">
          <table className="billing-admin__table">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Label</th>
                <th className="num">Quantity</th>
                <th className="num">Included</th>
                <th className="num">Billable</th>
                <th className="num">Unit price</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--uh-text-secondary)' }}>
                    No line items on this invoice.
                  </td>
                </tr>
              )}
              {lines.map((li, idx) => {
                const kind = li.Kind || li.kind;
                return (
                  <tr key={idx}>
                    <td><span className={`billing-admin__kind-chip billing-admin__kind-chip--${kind}`}>{kindLabel(kind)}</span></td>
                    <td>{li.Label || li.label}</td>
                    <td className="num">{Number(li.Quantity || li.quantity || 0).toLocaleString()}</td>
                    <td className="num">{Number(li.IncludedQuantity || li.included_quantity || 0).toLocaleString()}</td>
                    <td className="num">{Number(li.BillableQuantity || li.billable_quantity || 0).toLocaleString()}</td>
                    <td className="num">{formatCurrency(li.UnitPriceCents || li.unit_price_cents || 0, currency)}</td>
                    <td className="num"><strong>{formatCurrency(li.AmountCents || li.amount_cents || 0, currency)}</strong></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="billing-admin__receipt-total-row">
          <span>Total due</span>
          <span className="billing-admin__receipt-total-amount">{formatCurrency(totalCents, currency)}</span>
        </div>
      </div>

      {invoice.Notes || invoice.notes ? (
        <SectionCard title="Notes" className="billing-admin__no-print">
          <p style={{ margin: 0 }}>{invoice.Notes || invoice.notes}</p>
        </SectionCard>
      ) : null}
    </div>
  );
}
